import { useState, useCallback } from 'react';

interface UseDragAndDropProps {
    onDrop: (files: File[]) => void;
}

interface UseDragAndDropReturn {
    isDragActive: boolean;
    dragHandlers: {
        onDragEnter: (e: React.DragEvent) => void;
        onDragLeave: (e: React.DragEvent) => void;
        onDragOver: (e: React.DragEvent) => void;
        onDrop: (e: React.DragEvent) => void;
    };
}

/**
 * Custom hook for handling drag-and-drop file operations
 * 
 * @param onDrop - Callback function called when files are dropped
 * @returns Object containing drag state and event handlers
 */
export const useDragAndDrop = ({ onDrop }: UseDragAndDropProps): UseDragAndDropReturn => {
    const [isDragActive, setIsDragActive] = useState(false);

    const handleDragEnter = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);

        const files = Array.from(e.dataTransfer.files);
        onDrop(files);
    }, [onDrop]);

    return {
        isDragActive,
        dragHandlers: {
            onDragEnter: handleDragEnter,
            onDragLeave: handleDragLeave,
            onDragOver: handleDragOver,
            onDrop: handleDrop
        }
    };
};
